<template>
    <div class="user-groups-page">
        <header class="user-groups-heading">
            <div><h1>Группы сотрудников</h1><p class="text-muted mb-0">Готовые составы для быстрого добавления участников.</p></div>
            <b-button size="sm" variant="success" :disabled="busy || loading || loadFailed" @click="openName()"><i class="fas fa-plus mr-1" aria-hidden="true"/>Создать группу</b-button>
        </header>
        <b-alert :show="!!error" variant="danger">{{ error }} <b-button v-if="loadFailed" size="sm" variant="link" @click="loadGroups()">Повторить загрузку</b-button></b-alert>
        <p v-if="loading && !groups.length" class="user-groups-state" role="status"><b-spinner small class="mr-2"/>Загружаем группы…</p>
        <div v-else-if="!groups.length && !loadFailed" class="user-groups-state"><h2>Пока нет групп</h2><p class="text-muted mb-0">Создайте группу и выберите сотрудников или роли на объекте.</p></div>
        <div v-else-if="groups.length" class="user-groups-layout" :class="{'user-groups-layout--detail': mobileDetail}">
            <aside class="user-groups-sidebar" aria-label="Список групп">
                <div class="user-groups-sidebar__search">
                    <b-form-input v-model="search" size="sm" type="search" aria-label="Поиск группы или сотрудника" placeholder="Группа или сотрудник"/>
                    <small class="text-muted">Групп: {{ filteredGroups.length }}<template v-if="search"> из {{ groups.length }}</template></small>
                </div>
                <ul class="user-groups-list">
                    <li v-for="group in filteredGroups" :key="group.id">
                        <button type="button" class="user-groups-list__item" :class="{'is-selected': selectedId === group.id}" :aria-current="selectedId === group.id ? 'true' : null" :disabled="busy || loading" @click="selectGroup(group.id)">
                            <span class="user-groups-list__name">{{ group.name }}</span>
                            <small>Сотрудников: {{ (group.users || []).length }}<template v-if="(group.object_roles || []).length"> · ролей: {{ group.object_roles.length }}</template></small>
                        </button>
                    </li>
                </ul>
                <p v-if="!filteredGroups.length" class="text-muted p-3 mb-0">Группы не найдены.</p>
            </aside>
            <div class="user-groups-content">
                <b-button size="sm" variant="link" class="user-groups-back" :disabled="busy" @click="mobileDetail = false"><i class="fas fa-arrow-left mr-1" aria-hidden="true"/>К списку групп</b-button>
                <div class="user-groups-feedback" aria-live="polite"><span v-if="busy"><b-spinner small class="mr-1"/>Сохраняем…</span><span v-else-if="saved" class="text-success"><i class="fas fa-check mr-1" aria-hidden="true"/>{{ saved }}</span></div>
                <GroupCard v-if="selectedGroup" :key="selectedGroup.id" :item="selectedGroup" :busy="busy || loading || loadFailed" @rename="openName(selectedGroup)" @delete="deleteGroup" @add="openAdd" @remove="removeUser" @admin="setAdmin" @roles="saveRoles"/>
            </div>
        </div>
        <b-modal v-model="nameVisible" :title="nameTarget ? 'Название группы' : 'Новая группа'" :ok-title="busy ? 'Сохраняем…' : nameTarget ? 'Сохранить' : 'Создать'" ok-variant="success" cancel-title="Отмена" :ok-disabled="busy || !name.trim()" :cancel-disabled="busy" @ok="saveName" @hide="preventBusyClose" @shown="focusName">
            <b-alert :show="!!modalError" variant="danger">{{ modalError }}</b-alert>
            <b-form @submit.prevent="saveName"><b-form-group label="Название" label-for="user-group-name" class="mb-0">
                <b-form-input id="user-group-name" ref="groupName" v-model="name" maxlength="255" :disabled="busy" placeholder="Например, снабжение" autocomplete="off"/>
            </b-form-group></b-form>
        </b-modal>
        <b-modal v-model="addVisible" title="Добавить сотрудников" size="lg" scrollable :ok-title="busy ? 'Добавляем…' : 'Добавить выбранных (' + selectedUsers.length + ')'" ok-variant="success" cancel-title="Отмена" :ok-disabled="busy || !selectedUsers.length || usersLoading || !!usersError" :cancel-disabled="busy" @ok="addUsers" @hide="preventBusyClose">
            <p class="text-muted">{{ selectedGroup && selectedGroup.name }}</p>
            <b-alert :show="!!modalError" variant="danger">{{ modalError }}</b-alert>
            <b-alert :show="!!usersError" variant="warning">{{ usersError }} <b-button size="sm" variant="link" @click="loadUsers">Повторить</b-button></b-alert>
            <b-form-input v-model="userSearch" type="search" aria-label="Поиск сотрудников для добавления" placeholder="Фамилия или имя" class="mb-2"/>
            <p v-if="usersLoading" role="status">Загружаем сотрудников…</p>
            <p v-else-if="!availableUsers.length && !usersError" class="text-muted my-3">{{ userSearch ? 'Сотрудники не найдены.' : 'Все доступные сотрудники уже в группе.' }}</p>
            <div v-for="user in availableUsers" :key="user.user_id" class="user-groups-picker-row">
                <b-form-checkbox v-model="selectedUsers" :value="String(user.user_id)" :disabled="busy" :aria-label="'Добавить: ' + personName(user)"/>
                <EmployeeLink :user="user" name-order="last-first"/>
            </div>
        </b-modal>
    </div>
</template>
<script>
import GroupCard from './GroupCard/GroupCard.vue';
import EmployeeLink from '@/components/EmployeeLink.vue';
import api from '@/utils/api';
import ajax from '@/utils/ajax';
import {personName, normalizeSearch, matchesPerson} from '@/utils/userGroupParticipants';
export default {
    name: 'UserGroups', components: {GroupCard, EmployeeLink},
    data: () => ({groups: [], selectedId: null, search: '', mobileDetail: false, loading: false, loadFailed: false, busy: false, error: '', saved: '',
        nameVisible: false, nameTarget: null, name: '', modalError: '', addVisible: false, users: [], usersLoading: false, usersError: '', userSearch: '', selectedUsers: []}),
    computed: {
        selectedGroup() { return this.groups.find(group => group.id === this.selectedId); },
        filteredGroups() { return this.groups.filter(group => normalizeSearch(group.name).includes(normalizeSearch(this.search)) || (group.users || []).some(user => matchesPerson(user, this.search))); },
        availableUsers() {
            const members = new Set((this.selectedGroup && this.selectedGroup.users || []).map(user => String(user.user_id)));
            return this.users.filter(user => user.active !== false && user.isBot !== true && !members.has(String(user.user_id)) && matchesPerson(user, this.userSearch));
        }
    },
    mounted() { this.loadGroups(); },
    methods: {
        personName,
        message(error, fallback) { const data = error.response && error.response.data; return data && typeof data.msg === 'string' ? data.msg : fallback; },
        async loadGroups(preferredId) {
            if (this.loading) return false;
            this.loading = true; this.loadFailed = false;
            try {
                const {data} = await api.get('/api/user-groups', {ignoreModal: true});
                if (this._isDestroyed) return false;
                this.groups = data;
                const id = preferredId || this.selectedId;
                this.selectedId = data.some(group => group.id === id) ? id : data.length ? data[0].id : null;
                this.error = ''; return true;
            } catch (error) { this.loadFailed = true; this.error = this.message(error, 'Не удалось загрузить группы.'); return false; }
            finally { this.loading = false; }
        },
        selectGroup(id) { this.selectedId = id; this.mobileDetail = true; this.saved = ''; },
        openName(group) { this.nameTarget = group && group.id || null; this.name = group && group.name || ''; this.modalError = ''; this.nameVisible = true; },
        focusName() { this.$refs.groupName.focus(); },
        preventBusyClose(event) { if (this.busy) event.preventDefault(); },
        async saveName(event) {
            if (event) event.preventDefault();
            if (this.busy || !this.name.trim()) return;
            this.busy = true; this.modalError = ''; this.saved = '';
            try {
                const payload = {name: this.name.trim()};
                const {data} = this.nameTarget ? await api.put(`/api/user-groups/${this.nameTarget}`, payload, {ignoreModal: true}) : await api.post('/api/user-groups', payload, {ignoreModal: true});
                this.busy = false; this.nameVisible = false; this.search = ''; this.mobileDetail = true;
                if (await this.loadGroups(data.id)) this.saved = 'Сохранено';
            } catch (error) { this.modalError = this.message(error, 'Не удалось сохранить название.'); }
            finally { this.busy = false; }
        },
        async mutate(payload, success = 'Сохранено') {
            if (this.busy || this.loading || this.loadFailed || !this.selectedGroup) return false;
            const id = this.selectedId; this.busy = true; this.error = ''; this.saved = '';
            try {
                await api.put(`/api/user-groups/${id}`, payload, {ignoreModal: true});
                if (await this.loadGroups(id)) this.saved = success;
                return true;
            } catch (error) {
                const message = this.message(error, 'Не удалось получить результат сохранения. Проверьте текущий состав группы.');
                await this.loadGroups(id); this.error = message; return false;
            }
            finally { this.busy = false; }
        },
        setAdmin(user) { return this.mutate({users: [{...user, action: 'edit'}]}); },
        saveRoles(roles) { return this.mutate({object_roles: roles}); },
        async removeUser(user) {
            const id = this.selectedId;
            const confirmed = await this.$bvModal.msgBoxConfirm(`Убрать ${personName(user)} из группы «${this.selectedGroup.name}»?`, {title: 'Убрать участника', okTitle: 'Убрать', cancelTitle: 'Отмена', okVariant: 'danger', centered: true});
            if (confirmed && id === this.selectedId) await this.mutate({users: [{user_id: user.user_id, action: 'remove'}]}, 'Участник убран');
        },
        async deleteGroup() {
            if (this.busy || !this.selectedGroup) return;
            const group = this.selectedGroup;
            const confirmed = await this.$bvModal.msgBoxConfirm(`Удалить группу «${group.name}»? Уже созданные Telegram-группы останутся без изменений.`, {title: 'Удаление группы', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true});
            if (!confirmed) return;
            this.busy = true; this.error = ''; this.saved = '';
            try { await api.delete(`/api/user-groups/${group.id}`, {ignoreModal: true}); this.mobileDetail = false; await this.loadGroups(); }
            catch (error) { this.error = this.message(error, 'Не удалось удалить группу.'); }
            finally { this.busy = false; }
        },
        openAdd() { this.selectedUsers = []; this.userSearch = ''; this.modalError = ''; this.addVisible = true; this.loadUsers(); },
        async loadUsers() {
            if (this.usersLoading) return;
            this.usersLoading = true; this.usersError = '';
            try { this.users = await ajax.getShortUsers({telegramPolicy: true}); }
            catch (error) { this.usersError = 'Не удалось загрузить сотрудников.'; }
            finally { this.usersLoading = false; }
        },
        async addUsers(event) {
            event.preventDefault(); if (this.busy || !this.selectedUsers.length) return;
            const ok = await this.mutate({users: this.selectedUsers.map(user_id => ({user_id: Number(user_id), action: 'create'}))}, 'Сотрудники добавлены');
            if (ok) this.addVisible = false; else this.modalError = this.error;
        }
    }
};
</script>
<style scoped>
.user-groups-page {padding: 16px; max-width: 1440px; margin: 0 auto; font-size: 14px;}
.user-groups-heading {display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px;}
.user-groups-heading h1 {font-size: 20px; font-weight: 600; margin: 0 0 4px;}
.user-groups-heading .btn {flex-shrink: 0;}
.user-groups-layout {display: grid; grid-template-columns: minmax(240px, 300px) minmax(0, 1fr); gap: 16px; align-items: start;}
.user-groups-sidebar, .user-groups-content, .user-groups-state {background: #fff; border: 1px solid #dee2e6; border-radius: 6px; min-width: 0;}
.user-groups-sidebar__search {padding: 12px; border-bottom: 1px solid #edf0f2;}
.user-groups-sidebar__search small {display: block; margin-top: 8px;}
.user-groups-list {list-style: none; padding: 0; margin: 0;}
.user-groups-list__item {width: 100%; background: #fff; border: 0; border-bottom: 1px solid #edf0f2; padding: 12px; text-align: left; color: #343a40; border-left: 3px solid transparent;}
.user-groups-list__item:hover {background: #f8f9fa;}
.user-groups-list__item:focus-visible {outline: 2px solid #007bff; outline-offset: -2px;}
.user-groups-list__item.is-selected {background: #f1f8f3; border-left-color: #28a745;}
.user-groups-list__name {display: block; font-weight: 600; overflow-wrap: anywhere; margin-bottom: 4px;}
.user-groups-list__item small {color: #6c757d;}
.user-groups-feedback {padding: 8px 16px 0; font-size: 12px; min-height: 26px;}
.user-groups-back {display: none;}
.user-groups-state {padding: 32px 20px; text-align: center;}
.user-groups-state h2 {font-size: 17px;}
.user-groups-picker-row {display: flex; align-items: center; gap: 8px; padding: 9px 0; border-bottom: 1px solid #edf0f2;}
.user-groups-picker-row >>> .employee-link {white-space: normal; text-align: left;}
@media (max-width: 767px) {
    .user-groups-page {padding: 12px;}
    .user-groups-heading {align-items: flex-start; flex-wrap: wrap;}
    .user-groups-layout {display: block;}
    .user-groups-content {display: none;}
    .user-groups-layout--detail .user-groups-content {display: block;}
    .user-groups-layout--detail .user-groups-sidebar {display: none;}
    .user-groups-back {display: inline-block; margin: 8px 8px 0;}
    .user-groups-sidebar input {font-size: 16px;}
}
</style>
